import {ssrSafeDocument, ssrSafeWindow} from '@github-ui/ssr-utils'
import type {INPMetric} from './metric'
import {InteractionProcessor, type CallbackRegistration} from './interaction-processor'
import {BaseObserver, type ReportOptions} from '../base/observer'
import {SOFT_NAV_STATE} from '@github-ui/soft-nav/states'
import {whenIdleOrHidden} from '../utils/when-idle-or-hidden'

const supportsINP =
  // eslint-disable-next-line compat/compat
  ssrSafeWindow && 'PerformanceEventTiming' in ssrSafeWindow && 'interactionId' in PerformanceEventTiming.prototype

/*
 * The INPObserver is responsible for listening to Performance events and routing them to the InteractionProcessor.
 * It also manages resetting INP and reporting it when navigating or hiding a page.
 */
export class INPObserver extends BaseObserver<INPMetric, PerformanceEventTiming> {
  /** Set when a hard navigation is initiated (beforeunload). Distinguishes hard-nav departure from tab switch. */
  hardNavPending = false

  get softNavEventToListen() {
    return SOFT_NAV_STATE.START
  }

  initializeProcessor() {
    return new InteractionProcessor()
  }

  override get supported(): boolean {
    return !!supportsINP
  }

  override setupListeners() {
    super.setupListeners()

    // Track when the page is actually being destroyed (hard navigation or tab close).
    // beforeunload fires for hard navigations and tab/window close, but NOT for
    // tab switches — letting report() distinguish the two and only skip
    // takeRecords() when the entries would have unreliable durations.
    ssrSafeWindow?.addEventListener('beforeunload', () => {
      this.hardNavPending = true
    })
  }

  observe(initialLoad = true) {
    if (!supportsINP) return

    this.url = ssrSafeWindow?.location.href
    this.observer = new PerformanceObserver(list => {
      whenIdleOrHidden(() => {
        this.entryProcessor.processEntries(list.getEntries() as PerformanceEventTiming[])
      })
    })

    if (initialLoad) {
      return this.observeEvents(initialLoad)
    }

    // SOFT_NAV_STATE.RENDER is dispatched when the soft navigation finished rendering.
    // That means that the previous page is fully hidden so we can start listening for new events.
    ssrSafeDocument?.addEventListener(SOFT_NAV_STATE.RENDER, () => {
      this.observeEvents(initialLoad)
    })
  }

  observeEvents(initialLoad: boolean) {
    if (!this.observer) return

    this.observer.observe({type: 'first-input', buffered: initialLoad})
    this.observer.observe({
      type: 'event',
      durationThreshold: 40,
      // buffered events are important on first page load since we may have missed
      // a few until the observer was set up.
      buffered: initialLoad,
    })
  }

  registerCallback(callback: CallbackRegistration) {
    this.interactionProcessor.registeredCallbacks.add(callback)
  }

  override report(options?: ReportOptions) {
    // Skip takeRecords() only during actual hard-nav departure.
    //
    // When a hard navigation is triggered (e.g. clicking an UnderlineNav tab
    // to another page), the browser fires visibilitychange before the page is
    // destroyed. takeRecords() at that point forces the browser to flush the
    // PerformanceEventTiming for the navigation click, but its duration is
    // unreliable — the "next paint" never arrives on this page, so the timing
    // reflects teardown overhead rather than genuine page responsiveness.
    //
    // We must NOT skip takeRecords() on tab switches (visibilitychange without
    // navigation), because those entries are valid in-page interactions that
    // may not have been delivered to the PerformanceObserver callback yet.
    // The hardNavPending flag (set via beforeunload) distinguishes the two.
    //
    // During soft-nav:start, takeRecords() is always called (isPageHide=false)
    // to capture the last in-page interaction before resetting.
    const isHardNavDeparture = options?.isPageHide && this.hardNavPending

    if (!isHardNavDeparture) {
      const entries = this.observer?.takeRecords()

      if (entries && entries.length) {
        this.entryProcessor.processEntries(entries as PerformanceEventTiming[])
      }
    }

    super.report(options)
  }

  get interactionProcessor(): InteractionProcessor {
    return this.entryProcessor as InteractionProcessor
  }
}
